import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, Button } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { Icon } from '../components/icons';
import { formatDate, formatMoney } from '../utils/format';

interface Transaction {
    id: number;
    transactionDate: string;
    who: string;
    amount: number;
    description: string;
    isSettlement: boolean | null;
}

interface TransactionHistoryPageProps {
    startDate: string | null;
    endDate: string | null;
    schoolYear: string;
}

type SortConfig = { key: keyof Transaction; direction: 'asc' | 'desc' };

const MOBILE_SORT_OPTIONS: { label: string; value: string; config: SortConfig }[] = [
    { label: 'Data — od najnowszych', value: 'date-desc', config: { key: 'transactionDate', direction: 'desc' } },
    { label: 'Data — od najstarszych', value: 'date-asc', config: { key: 'transactionDate', direction: 'asc' } },
    { label: 'Kto — A do Z', value: 'who-asc', config: { key: 'who', direction: 'asc' } },
    { label: 'Kwota — malejąco', value: 'amount-desc', config: { key: 'amount', direction: 'desc' } },
    { label: 'Kwota — rosnąco', value: 'amount-asc', config: { key: 'amount', direction: 'asc' } },
];

const TransactionHistoryPage: React.FC<TransactionHistoryPageProps> = ({ startDate, endDate, schoolYear }) => {
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [duplicates, setDuplicates] = useState<Transaction[]>([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [sortConfig, setSortConfig] = useState<SortConfig | null>({ key: 'transactionDate', direction: 'desc' });

    const fetchHistory = () => {
        if (startDate && endDate) {
            axios.get<Transaction[]>('/api/payments/history', {
                params: { startDate, endDate, schoolYear }
            }).then(response => setTransactions(response.data))
              .catch(error => console.error('Error fetching transaction history:', error));
        }
    };

    const fetchDuplicates = () => {
        axios.get<Transaction[]>('/api/payments/duplicates', {
            params: { schoolYear }
        }).then(response => setDuplicates(response.data))
          .catch(error => console.error('Error fetching duplicates:', error));
    };

    useEffect(() => {
        if (startDate && endDate) {
            let cancelled = false;
            setLoading(true);
            Promise.all([
                axios.get<Transaction[]>('/api/payments/history', { params: { startDate, endDate, schoolYear } }),
                axios.get<Transaction[]>('/api/payments/duplicates', { params: { schoolYear } })
            ]).then(([historyRes, duplicatesRes]) => {
                if (cancelled) return;
                setTransactions(historyRes.data);
                setDuplicates(duplicatesRes.data);
                setLoading(false);
            }).catch(error => {
                if (cancelled) return;
                console.error('Error fetching data:', error);
                setLoading(false);
            });
            return () => { cancelled = true; };
        }
    }, [startDate, endDate, schoolYear]);

    const handleIgnoreDuplicate = (id: number) => {
        axios.patch(`/api/payments/${id}/ignore-duplicate`)
            .then(() => fetchDuplicates())
            .catch(e => console.error('Error ignoring duplicate:', e));
    };

    const handleDeleteDuplicate = (id: number) => {
        if (window.confirm('Czy na pewno chcesz usunąć tę transakcję?')) {
            axios.delete(`/api/payments/${id}`)
                .then(() => {
                    fetchDuplicates();
                    fetchHistory();
                })
                .catch(e => console.error('Error deleting duplicate:', e));
        }
    };

    const handleSettlementChange = (id: number, isSettlement: boolean) => {
        axios.patch(`/api/payments/${id}/settlement`, { isSettlement })
            .then(() => {
                setTransactions(transactions.map(t => t.id === id ? { ...t, isSettlement } : t));
            })
            .catch(error => {
                console.error('Error updating settlement status:', error);
            });
    };

    const handleSort = (key: keyof Transaction) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const visibleTransactions = React.useMemo(() => {
        const needle = query.trim().toLowerCase();
        const filtered = needle
            ? transactions.filter(t =>
                t.who.toLowerCase().includes(needle) ||
                t.description.toLowerCase().includes(needle) ||
                formatDate(t.transactionDate).includes(needle) ||
                String(t.amount).includes(needle))
            : [...transactions];

        if (sortConfig !== null) {
            filtered.sort((a, b) => {
                const key = sortConfig.key;
                let aValue: any = a[key];
                let bValue: any = b[key];

                // Handle null/undefined for isSettlement
                if (key === 'isSettlement') {
                    aValue = aValue ? 1 : 0;
                    bValue = bValue ? 1 : 0;
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return filtered;
    }, [transactions, sortConfig, query]);

    const sortHeader = (column: keyof Transaction, label: string, align?: 'num') => {
        const active = sortConfig?.key === column;
        return (
            <th
                key={column}
                scope="col"
                className={align}
                aria-sort={active ? (sortConfig!.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
                <button type="button" className={`sort-btn${active ? ' active' : ''}`} onClick={() => handleSort(column)}>
                    {label}
                    {active && <Icon name={sortConfig!.direction === 'asc' ? 'sortUp' : 'sortDown'} />}
                </button>
            </th>
        );
    };

    const mobileSortValue = MOBILE_SORT_OPTIONS.find(o => o.config.key === sortConfig?.key && o.config.direction === sortConfig?.direction)?.value ?? '';

    if (loading) {
        return (
            <div className="page">
                <PageHeader title="Historia transakcji" />
                <div className="loading-state"><span className="spinner-border spinner-border-sm" role="status" />Ładowanie transakcji…</div>
            </div>
        );
    }

    return (
        <div className="page">
            <PageHeader
                title="Historia transakcji"
                subtitle={`Rok szkolny ${schoolYear} · ${transactions.length} ${transactions.length === 1 ? 'transakcja' : 'transakcji'}`}
            />

            {duplicates.length > 0 && (
                <section className="panel surface panel-attention">
                    <div className="panel-head">
                        <h2 className="section-title">Potencjalne duplikaty wpłat</h2>
                        <span className="count-badge">{duplicates.length}</span>
                    </div>
                    <p className="section-hint">Te transakcje wyglądają na wgrane dwa razy. Usuń zdublowaną wpłatę albo oznacz ją jako poprawną.</p>
                    <div className="table-scroll">
                        <table className="table data-table table-stack">
                            <thead>
                                <tr>
                                    <th scope="col">Data</th>
                                    <th scope="col">Kto</th>
                                    <th className="num" scope="col">Kwota</th>
                                    <th scope="col">Opis</th>
                                    <th className="actions" scope="col"><span className="visually-hidden">Akcje</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {duplicates.map((transaction) => (
                                    <tr key={transaction.id}>
                                        <td data-label="Data" className="nowrap">{formatDate(transaction.transactionDate)}</td>
                                        <td data-label="Kto">{transaction.who}</td>
                                        <td data-label="Kwota" className="num">{formatMoney(transaction.amount)}</td>
                                        <td data-label="Opis">{transaction.description}</td>
                                        <td data-label="" className="actions">
                                            <div className="d-inline-flex gap-2">
                                                <Button size="sm" variant="outline-secondary" onClick={() => handleIgnoreDuplicate(transaction.id)}>
                                                    <Icon name="check" size={15} />
                                                    To poprawna wpłata
                                                </Button>
                                                <Button size="sm" variant="outline-danger" onClick={() => handleDeleteDuplicate(transaction.id)}>
                                                    <Icon name="trash" size={15} />
                                                    Usuń
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            <div className="surface table-card">
                <div className="toolbar">
                    <div className="search">
                        <Icon name="search" size={16} />
                        <Form.Control
                            type="search"
                            placeholder="Szukaj po osobie, opisie, kwocie lub dacie"
                            aria-label="Szukaj transakcji"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                        />
                    </div>
                    <Form.Select
                        className="d-md-none w-auto"
                        aria-label="Sortowanie"
                        value={mobileSortValue}
                        onChange={e => {
                            const option = MOBILE_SORT_OPTIONS.find(o => o.value === e.target.value);
                            if (option) setSortConfig(option.config);
                        }}
                    >
                        {mobileSortValue === '' && <option value="">Sortuj…</option>}
                        {MOBILE_SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </Form.Select>
                    <span className="toolbar-meta" aria-live="polite">
                        {query.trim() ? `Znaleziono ${visibleTransactions.length} z ${transactions.length}` : `${transactions.length} transakcji`}
                    </span>
                </div>

                {visibleTransactions.length === 0 ? (
                    <div className="empty-state">
                        {transactions.length === 0 ? 'Brak transakcji w tym roku szkolnym.' : 'Żadna transakcja nie pasuje do wyszukiwania.'}
                    </div>
                ) : (
                    <div className="table-scroll">
                        <table className="table data-table table-stack">
                            <thead>
                                <tr>
                                    {sortHeader('isSettlement', 'Rozliczenia')}
                                    {sortHeader('transactionDate', 'Data')}
                                    {sortHeader('who', 'Kto')}
                                    {sortHeader('amount', 'Kwota', 'num')}
                                    {sortHeader('description', 'Opis')}
                                </tr>
                            </thead>
                            <tbody>
                                {visibleTransactions.map((transaction) => (
                                    <tr key={transaction.id}>
                                        <td data-label="Rozliczenie">
                                            <Form.Check
                                                type="checkbox"
                                                aria-label={`Rozliczenie: ${transaction.who}, ${formatDate(transaction.transactionDate)}`}
                                                checked={!!transaction.isSettlement}
                                                onChange={(e) => handleSettlementChange(transaction.id, e.target.checked)}
                                            />
                                        </td>
                                        <td data-label="Data" className="nowrap">{formatDate(transaction.transactionDate)}</td>
                                        <td data-label="Kto" className="nowrap">{transaction.who}</td>
                                        <td data-label="Kwota" className="num">{formatMoney(transaction.amount)}</td>
                                        <td data-label="Opis">{transaction.description}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TransactionHistoryPage;
